import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkedText } from "@/components/atoms/linked-text";

/** Speling voor subpixel-afronding: onder een hele pixel is er niets afgekapt. */
const SPELING_PX = 1;

/**
 * Een stuk briefingtekst van de planner: de links erin aanklikbaar, en ingeklapt zodra hij niet in
 * vier regels past.
 *
 * **De knop volgt de meting, niet de lengte.** Hij stond hier eerst op `text.length > 220`, en dat
 * was op twee manieren mis. 221 tekens die toevallig precies in vier regels pasten kregen een "Meer
 * lezen" die niets deed, en op een breed scherm gebeurde dat al bij twee regels. Andersom kreeg een
 * kledinglijstje van 137 tekens met een witregel tussen elke regel géén rem: 15 regels, 315px, en
 * alles eronder van het scherm geduwd. Zo schrijven planners echt.
 *
 * Een tekenteller kan dat niet weten, want hij kent de schermbreedte niet en telt een witregel als
 * één teken. Dus: altijd klemmen, en na de layout vragen of er iets buiten de klem valt.
 * `scrollHeight > clientHeight` is precies die vraag, en hij beantwoordt zichzelf opnieuw bij een
 * draaiing of een resize.
 *
 * Meten gebeurt alleen in de ingeklapte stand. Uitgeklapt is er per definitie niets afgekapt, en
 * dan zou de meting de knop weghalen waarmee je weer dicht wilt.
 */
export function BriefingText({ text }: { text: string }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isClipped, setIsClipped] = useState(false);
  const paragraph = useRef<HTMLParagraphElement>(null);

  // Een andere tekst is een andere beslissing, dus ingeklapt beginnen. Tijdens het renderen en niet
  // in een effect: een effect laat één frame de nieuwe tekst onafgeklemd zien.
  const [renderedText, setRenderedText] = useState(text);
  if (renderedText !== text) {
    setRenderedText(text);
    setIsOpen(false);
  }

  useLayoutEffect(() => {
    const element = paragraph.current;
    if (!element || isOpen) {
      return;
    }

    const meet = () => {
      setIsClipped(element.scrollHeight - element.clientHeight > SPELING_PX);
    };
    meet();

    // Draaien of splitscreen verandert de regelval, en daarmee het antwoord. Dezelfde guard als
    // `content.tsx`: jsdom en oudere webviews kennen ResizeObserver niet, en dan is de meting bij
    // de eerste layout nog altijd beter dan een tekenteller.
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(meet);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, isOpen]);

  return (
    <div className="flex w-full flex-col items-start gap-1">
      <p
        ref={paragraph}
        data-testid="briefing-text"
        className={`whitespace-pre-wrap text-md leading-[1.5] text-txt-secondary ${
          isOpen ? "" : "line-clamp-4"
        }`}
      >
        <LinkedText text={text} />
      </p>
      {isClipped && (
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="touch-target text-md font-semibold text-txt-brand"
        >
          {t(isOpen ? "routes:shift-detail.read-less" : "routes:shift-detail.read-more")}
        </button>
      )}
    </div>
  );
}
